import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads } from "../api/admin-api.js";
import type { DepthSource, LadderVersion } from "../api/types.js";
import { bps, count, utc } from "../format.js";
import {
  Blank,
  ErrorNote,
  Note,
  Panel,
  Pill,
  ScreenHeader,
  Spinner,
  StatRow,
  Table,
  Td,
  Th,
  Tile,
  inputClass,
} from "../ui.js";

/**
 * The live ask ladder, per tier, next to the sources it was built from and the parameters that
 * shaped it.
 *
 * TWO READS, TWO AUDIENCES. The ladder is `GET /v1/orderbook` — exactly what a buyer sees, with
 * the class per level and never a source (order-book.md §2). The sources are
 * `GET /v1/admin/orderbook/sources` (viewer): the `supply_depth` rows behind the ladder, WITH the
 * source id, which only the operator reads. The public book is not changed by this screen.
 */

// Every rental term, shortest first (core TIER_IDS, owner 2026-11-01).
const TIERS = ["5m", "10m", "15m", "30m", "1h", "3h", "6h", "12h", "1d", "2d", "3d", "7d", "14d", "30d"] as const;

const CLASS_HELP: Record<string, string> = {
  instant: "own capacity — fund wallets, free now",
  market: "an API provider that quoted",
  deep: "JustLend, the supply of last resort",
};

export function OrderBookScreen() {
  const [tier, setTier] = useState<string>("1h");
  const [resource, setResource] = useState("energy");

  const book = useResource(
    (signal) => {
      void signal;
      return adminReads.orderBook({ resource, tier });
    },
    [resource, tier],
  );
  const ladder = useResource((signal) => {
    void signal;
    return adminReads.ladderParameters({ limit: 1 });
  }, []);
  const sources = useResource(
    (signal) => {
      void signal;
      return adminReads.depthSources({ resource, tier });
    },
    [resource, tier],
  );
  const best = book.data?.levels[0] ?? null;
  const sourceRows = sources.data?.data ?? [];
  const bookNote = book.error !== null ? book.error.message : book.data == null ? "loading…" : `as of ${utc(book.data.as_of)}`;

  const levels = book.data?.levels ?? [];
  let cumulative = 0;

  return (
    <>
      <ScreenHeader
        title="Order book"
        lead="The ask ladder as a buyer sees it, rebuilt every few seconds from every supply source with its real cost. Prices are monotonic and no level sits below the floor."
        action={
          <div class="flex gap-2">
            <select
              class={`${inputClass} w-auto`}
              aria-label="Resource"
              value={resource}
              onChange={(event) => setResource((event.currentTarget as HTMLSelectElement).value)}
            >
              <option value="energy">energy</option>
              <option value="bandwidth">bandwidth</option>
            </select>
            <select
              class={`${inputClass} w-auto`}
              aria-label="Tier"
              value={tier}
              onChange={(event) => setTier((event.currentTarget as HTMLSelectElement).value)}
            >
              {TIERS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        }
      />

      <StatRow>
        <Tile
          icon="book"
          label="Book depth"
          value={book.data == null ? null : count(book.data.depth)}
          unit={resource}
          note={bookNote}
        />
        <Tile
          icon="list"
          label="Levels"
          value={book.data == null ? null : String(book.data.levels.length)}
          note={book.data == null ? bookNote : "Price levels a buyer can walk right now."}
        />
        <Tile
          icon="tag"
          label="Best ask"
          value={best === null ? null : String(best.price_sun)}
          unit="SUN/unit"
          note={best === null ? "The book is empty for this tier." : `${count(best.amount)} units at the top level, class ${best.class}.`}
        />
        <Tile
          icon="shield"
          label="Floor"
          value={book.data == null || book.data.floor_sun === null ? null : String(book.data.floor_sun)}
          unit="SUN/unit"
          note={book.data == null ? bookNote : book.data.floor_sun === null ? "No floor published for this tier." : "No level may sit below it."}
        />
      </StatRow>

      <div class="space-y-3">
        <Panel
          title={`Ladder — ${resource} / ${tier}`}
          subtitle={
            book.data === undefined || book.data === null
              ? undefined
              : `as of ${utc(book.data.as_of)}, depth ${count(book.data.depth)}, floor ${
                  book.data.floor_sun === null ? "—" : `${book.data.floor_sun} SUN`
                }`
          }
        >
          {book.loading ? (
            <Spinner />
          ) : book.error !== null ? (
            <ErrorNote error={book.error} onRetry={() => book.reload()} />
          ) : (
            <Table
              rows={levels.length}
              empty="The book is empty for this tier. With no depth published there is nothing to walk, and a quote falls back to the single published price."
              head={
                <tr>
                  <Th numeric>#</Th>
                  <Th>Class</Th>
                  <Th>What that class is</Th>
                  <Th numeric>Price (SUN/unit)</Th>
                  <Th numeric>Amount</Th>
                  <Th numeric>Cumulative</Th>
                </tr>
              }
            >
              {levels.map((level, index) => {
                cumulative += level.amount;
                return (
                  <tr key={`${level.class}-${index}`}>
                    <Td numeric>{index + 1}</Td>
                    <Td>
                      <Pill value={level.class} />
                    </Td>
                    <Td>
                      <span class="text-ink-muted" title="No endpoint publishes the per-level source">
                        {CLASS_HELP[level.class] ?? "—"}
                      </span>
                    </Td>
                    <Td numeric>{level.price_sun}</Td>
                    <Td numeric>{count(level.amount)}</Td>
                    <Td numeric>{count(cumulative)}</Td>
                  </tr>
                );
              })}
            </Table>
          )}
        </Panel>

        <Panel
          title={`Sources — ${resource} / ${tier}`}
          subtitle={`The supply_depth rows the ladder is built from, cheapest first, with the source id buyers never see.${
            sources.data == null ? "" : ` A row older than ${sources.data.fresh_for_seconds} s is stale and the builder skips it.`
          }`}
        >
          {sources.loading && sources.data == null ? (
            <Spinner />
          ) : sources.error !== null ? (
            <ErrorNote error={sources.error} onRetry={() => sources.reload()} />
          ) : (
            <SourceTable rows={sourceRows} />
          )}
        </Panel>

        <Panel
          title="Ladder parameters in force"
          subtitle="Published in Prices. Nothing published means neutral: no bands, no margin, no hold-back."
        >
          {ladder.loading ? (
            <Spinner />
          ) : ladder.error !== null ? (
            <ErrorNote error={ladder.error} onRetry={() => ladder.reload()} />
          ) : (
            <ActiveLadder active={ladder.data?.active ?? null} />
          )}
        </Panel>
      </div>
    </>
  );
}

function ActiveLadder({ active }: { active: LadderVersion | null }) {
  if (active === null) {
    return (
      <Note>
        No version is in force. Provider levels are priced at cost and own capacity at today's
        retail price — the neutral state, not a missing configuration.
      </Note>
    );
  }
  return (
    <dl class="grid gap-x-6 gap-y-1 text-[12px] sm:grid-cols-2 lg:grid-cols-3">
      <Entry label="Effective from" value={utc(active.effective_from)} />
      <Entry label="Changed by" value={active.changed_by ?? "—"} />
      <Entry label="Reason" value={active.reason ?? "—"} />
      <Entry label="Margin, instant" value={bps(active.margin_instant_bp)} />
      <Entry label="Margin, market" value={bps(active.margin_market_bp)} />
      <Entry label="Margin, deep" value={bps(active.margin_deep_bp)} />
      <Entry label="Hold-back" value={bps(active.hold_back_bp)} />
      <Entry label="Slippage tolerance" value={bps(active.slippage_bp)} />
      <Entry label="Min level amount" value={count(active.min_level_amount)} />
      <Entry label="Max levels" value={count(active.max_levels)} />
      <Entry
        label="Utilisation bands"
        value={
          active.utilisation_bands === null || active.utilisation_bands.length === 0 ? (
            <Blank title="Own capacity is priced flat" />
          ) : (
            active.utilisation_bands
              .map((band) => `>${band.above_bps / 100}% +${band.surcharge_bps / 100}%`)
              .join(", ")
          )
        }
      />
    </dl>
  );
}

function Entry({ label, value }: { label: string; value: unknown }) {
  return (
    <div class="flex justify-between gap-3 border-b border-line py-1.5">
      <dt class="text-ink-muted">{label}</dt>
      <dd class="text-right text-ink">{value as never}</dd>
    </div>
  );
}

function SourceTable({ rows }: { rows: DepthSource[] }) {
  return (
    <Table
      rows={rows.length}
      empty="No source has reported depth for this tier. The worker's depth job writes these rows; start the worker, or register a fund wallet or a provider."
      head={
        <tr>
          <Th>Source</Th>
          <Th>Class</Th>
          <Th numeric>Available</Th>
          <Th numeric>Landed cost (SUN/unit)</Th>
          <Th numeric>Utilisation</Th>
          <Th>Observed</Th>
        </tr>
      }
    >
      {rows.map((row) => (
        <tr key={`${row.source_id}-${row.resource}-${row.tier}`}>
          <Td mono>{row.source_id}</Td>
          <Td>
            <Pill value={row.class} />
          </Td>
          <Td numeric>{count(row.available_amount)}</Td>
          <Td numeric>{(row.cost_centi_sun_per_unit / 100).toFixed(2)}</Td>
          <Td numeric>{row.utilisation_bp === null ? <Blank title="Only own capacity reports utilisation" /> : bps(row.utilisation_bp)}</Td>
          <Td class={row.fresh ? "" : "text-warning"}>
            {utc(row.observed_at)} <span class="num text-[10.5px] text-ink-muted">({count(row.age_seconds)} s ago)</span>
          </Td>
        </tr>
      ))}
    </Table>
  );
}
